import React from 'react'

import type { LogosType } from '@application/Interfaces/Logos/Logos'
import { Boxit4meTheme, IcontainersTheme } from '@application/Interfaces/Themes/Theme'
import type { Locale } from '@application/Types/Locale'
import { Theme } from '@icontainers/design-system/dist/theme'
import type { Organization } from '@infrastructure/Types/OrganizationType'
import { BoxIt4meLogos } from '@src/Application/Interfaces/Logos/BoxIt4meLogos'
import { IcontainerLogos } from '@src/Application/Interfaces/Logos/IcontainersLogos'
import { Opt } from '@src/Application/Types/Opt'

export const Icontainers: OrganizationSettings = {
    name: 'iContainers',
    type: 'icontainers',
    theme: IcontainersTheme,
    logos: IcontainerLogos,
    logoPath: '/resources/images/icontainers/logo.png',
    faviconIconPath: '/resources/images/icontainers/favicon.ico',
    infoEmailId: 'info@icontainers.com',
    languages: ['en', 'es', 'de', 'nl'],
    siteMetadata: {
        en: {
            twitterSite: '@icontainers',
            ogImageUrlPath: '/resources/images/icontainers/og_image.jpg',
            facebookLink: 'https://www.facebook.com/iContainersUS',
            twitterLink: 'https://twitter.com/iContainers',
            googlePlusLink: 'https://plus.google.com/+iContainersUSMiami',
            streetAddress: '901 Ponce de Leon Suite 504',
            addressLocality: 'Coral Gables',
            addressRegion: 'FL',
            postalCode: '33134',
            addressCountry: 'US',
        },
        es: {
            twitterSite: '@icontainersES',
            ogImageUrlPath: '/resources/images/icontainers/og_image.jpg',
            facebookLink: 'https://www.facebook.com/icontainers/',
            twitterLink: 'https://twitter.com/iContainersES',
            googlePlusLink: 'https://plus.google.com/+Icontainers',
            streetAddress: 'C/ Diputació 180, 5º',
            addressLocality: 'Barcelona',
            addressRegion: 'Barcelona',
            postalCode: '08011',
            addressCountry: 'ES',
        },
        de: {
            twitterSite: '@icontainers',
            ogImageUrlPath: '/resources/images/icontainers/og_image.jpg',
            facebookLink: 'https://www.facebook.com/iContainersUS',
            twitterLink: 'https://twitter.com/iContainers',
            googlePlusLink: 'https://plus.google.com/+iContainersUSMiami',
            streetAddress: '901 Ponce de Leon Suite 504',
            addressLocality: 'Coral Gables',
            addressRegion: 'FL',
            postalCode: '33134',
            addressCountry: 'US',
        },
        nl: {
            twitterSite: '@icontainers',
            ogImageUrlPath: '/resources/images/icontainers/og_image.jpg',
            facebookLink: 'https://www.facebook.com/iContainersUS',
            twitterLink: 'https://twitter.com/iContainers',
            googlePlusLink: 'https://plus.google.com/+iContainersUSMiami',
            streetAddress: '901 Ponce de Leon Suite 504',
            addressLocality: 'Coral Gables',
            addressRegion: 'FL',
            postalCode: '33134',
            addressCountry: 'US',
        },
    },
}

export const BoxIt4me: OrganizationSettings = {
    name: 'BoxIt4me',
    type: 'boxit4me',
    theme: Boxit4meTheme,
    logos: BoxIt4meLogos,
    logoPath: '/resources/images/boxit4me/logo.png',
    faviconIconPath: 'resources/images/boxit4me/favicon.ico',
    infoEmailId: 'help@boxit4me.com',
    languages: ['en', 'ar'],
    siteMetadata: {
        en: {
            facebookLink: 'https://www.facebook.com/Boxit4me/',
            twitterLink: 'https://twitter.com/Boxit4me',
            googlePlusLink: 'https://www.youtube.com/channel/UCJXKAm0c9UPFbDtJ_N5bG2Q',
            ogImageUrlPath: '/resources/images/boxit4me/og_image.jpg',
            streetAddress: 'Al Quoz Industrial Area 4',
            addressLocality: 'Dubai',
            addressRegion: 'Dubai',
            addressCountry: 'AE',
        },
        ar: {
            facebookLink: 'https://www.facebook.com/Boxit4me/',
            twitterLink: 'https://twitter.com/Boxit4me',
            googlePlusLink: 'https://www.youtube.com/channel/UCJXKAm0c9UPFbDtJ_N5bG2Q',
            ogImageUrlPath: '/resources/images/boxit4me/og_image.jpg',
            streetAddress: 'Al Quoz Industrial Area 4',
            addressLocality: 'Dubai',
            addressRegion: 'Dubai',
            addressCountry: 'AE',
        },
    },
}

const initialValue = {
    ...Icontainers,
}

export type OrganizationSettings = {
    name: string
    type: Organization
    theme: typeof Theme
    logos: LogosType
    logoPath: string
    faviconIconPath: string
    languages: Locale[]
    infoEmailId: string
    siteMetadata: Partial<Record<Locale, Record<string, string>>>
}

const Organizations: Record<Organization, OrganizationSettings> = {
    icontainers: Icontainers,
    boxit4me: BoxIt4me,
}

export const OrganizationContext = React.createContext<OrganizationSettings>(initialValue)

interface OrganizationProps {
    children: React.ReactNode
}

export const getOrganizationSettings = (): OrganizationSettings => {
    const organization = process.env.GATSBY_ORGANIZATION as Organization

    return Organizations[organization]
}

export const OrganizationProvider = ({ children }: OrganizationProps): JSX.Element => {
    const settings = getOrganizationSettings()
    return <OrganizationContext.Provider value={settings}>{children}</OrganizationContext.Provider>
}
